import type { CardSearchResponse, RiftboundCard } from '../types';
import type { DeckCardRef, DeckRune } from '../types/deck';
import { getApiBaseUrl } from '../constants/environment';
import { cardPrintCode, cardPrintGroupKey, filterSiblingPrintings, riftAtlasPrintCode } from './cardPrintings';
import { isAlternateArtCard, isPromoCard, isStandardSearchVariant } from './cardPrintVariant';

/** Chosen rune printing persisted on a deck. Absent means the basic domain rune. */
export interface DeckRunePrint {
  cardId: string;
  name: string;
  setCode?: string;
  number?: string;
  riftboundId?: string;
  originSet?: string;
  imageUrl?: string;
  thumbnailUrl?: string;
}

const DEFAULT_RUNE_CACHE = new Map<string, RiftboundCard>();

export function runePrintFromCard(card: Pick<
  RiftboundCard,
  'id' | 'name' | 'setCode' | 'number' | 'riftboundId' | 'imageUrl' | 'thumbnailUrl' | 'originSet'
>): DeckRunePrint {
  return {
    cardId: card.id,
    name: card.name,
    setCode: card.setCode,
    number: card.number,
    riftboundId: card.riftboundId,
    originSet: card.originSet,
    imageUrl: card.imageUrl,
    thumbnailUrl: card.thumbnailUrl,
  };
}

export function runePrintFromRef(card: DeckCardRef): DeckRunePrint {
  return {
    cardId: card.cardId,
    name: card.name,
    setCode: card.setCode,
    number: card.number,
    riftboundId: card.riftboundId,
    originSet: card.originSet,
    imageUrl: card.imageUrl,
    thumbnailUrl: card.thumbnailUrl,
  };
}

export function runeCardMatchesDomain(
  card: Pick<RiftboundCard, 'name' | 'domain' | 'cardType'> | DeckCardRef,
  domain: string,
): boolean {
  const cardType = 'cardType' in card ? card.cardType : undefined;
  if (cardType && cardType !== 'Rune') return false;
  const fromDomain = card.domain?.find((value) => value && value !== 'Colorless');
  if (fromDomain) return fromDomain.toLowerCase() === domain.toLowerCase();
  return cardPrintGroupKey(card.name) === cardPrintGroupKey(`${domain} Rune`);
}

/**
 * Label used in text export.
 *
 * TCG Arena keeps the variant name (`Fury Rune (Alternate Art)`) or a bare
 * collector code for a same-name showcase. Rift Atlas only accepts the
 * canonical rune name plus a bracketed collector code (`Fury Rune [OGN-007A]`).
 */
export function exportRuneLabel(
  rune: DeckRune,
  format: 'plain' | 'riftatlas' = 'plain',
): string {
  const base = `${rune.domain} Rune`;
  if (format === 'riftatlas') {
    const code = rune.print ? riftAtlasPrintCode(rune.print) : '';
    return code ? `${base} [${code}]` : base;
  }
  const print = rune.print;
  if (!print?.name) return base;
  const name = print.name.trim();
  if (name.toLowerCase() !== base.toLowerCase()) return name;
  const code = cardPrintCode({ ...print, setCode: print.setCode ?? '' });
  return code ? `${base} ${code}` : base;
}

export function runePrintToCard(rune: DeckRune): RiftboundCard {
  const print = rune.print;
  return {
    id: print?.cardId || `rune-${rune.domain.toLowerCase()}`,
    name: print?.name || `${rune.domain} Rune`,
    setCode: print?.setCode ?? '',
    number: print?.number,
    riftboundId: print?.riftboundId,
    cardType: 'Rune',
    keywords: [],
    domain: [rune.domain],
    imageUrl: print?.imageUrl,
    thumbnailUrl: print?.thumbnailUrl,
  };
}

/**
 * Alt art, promo, or other showcase rune. The Runes tab folds every print
 * onto the basic one, so only these say the player picked a specific art.
 */
export function isShowcaseRunePrint(card: RiftboundCard): boolean {
  return !isStandardSearchVariant(card);
}

/** Basic playable rune when several printings share the domain. */
export function pickDefaultRuneCard(
  cards: readonly RiftboundCard[],
  domain: string,
): RiftboundCard | undefined {
  const key = cardPrintGroupKey(`${domain} Rune`);
  const family = cards.filter(
    (card) =>
      card.cardType === 'Rune'
      && Boolean(card.setCode)
      && cardPrintGroupKey(card.name) === key,
  );
  const standard = family.filter((card) => isStandardSearchVariant(card) && !isPromoCard(card));
  const mainSet = family.filter((card) => !isPromoCard(card) && !isAlternateArtCard(card));
  const pool = standard.length > 0 ? standard : mainSet.length > 0 ? mainSet : family;
  return [...pool].sort((a, b) => {
    const set = String(a.setCode || '').localeCompare(String(b.setCode || ''));
    if (set !== 0) return set;
    return cardPrintCode(a).localeCompare(cardPrintCode(b));
  })[0];
}

export async function resolveDefaultRuneCard(domain: string): Promise<RiftboundCard | undefined> {
  const cached = DEFAULT_RUNE_CACHE.get(domain);
  if (cached) return cached;
  const params = new URLSearchParams({
    q: `${domain} Rune`,
    pageSize: '80',
    sort: 'name-asc',
  });
  const response = await fetch(`${getApiBaseUrl()}/api/cards?${params.toString()}`);
  if (!response.ok) return undefined;
  const data = (await response.json()) as CardSearchResponse;
  const seed = runePrintToCard({ domain, qty: 1 });
  const siblings = filterSiblingPrintings(seed, data.cards ?? []);
  const picked = pickDefaultRuneCard(siblings, domain);
  if (picked) DEFAULT_RUNE_CACHE.set(domain, picked);
  return picked;
}

/** Rune cards for the proxy sheet. A chosen print wins; otherwise the basic rune. */
export async function cardsForRuneExport(runes: readonly DeckRune[]): Promise<RiftboundCard[]> {
  const cards: RiftboundCard[] = [];
  for (const rune of runes) {
    if (rune.qty <= 0) continue;
    if (rune.print?.imageUrl || rune.print?.thumbnailUrl) {
      cards.push(runePrintToCard(rune));
      continue;
    }
    const fallback = await resolveDefaultRuneCard(rune.domain).catch(() => undefined);
    cards.push(fallback ?? runePrintToCard(rune));
  }
  return cards;
}
